import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Lock, Plus } from "lucide-react";
import AmountCell from "./AmountCell";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../ui/tooltip";
import { CATEGORIES, CATEGORY_FY_KEY, MONTHS, fmtSEK, g } from "./format";

function TmRow({ data, summary, tmYtd, onDrilldown }) {
  return (
    <tr className="border-b border-slate-200 bg-[#F8FAFC]" data-testid="tm-row">
      <td className="py-2.5 px-3 sticky left-0 bg-[#F8FAFC] z-10">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-slate-900">Time & Material</span>
          <span className="text-[10px] font-semibold text-slate-400 uppercase">from Resource Planning</span>
        </div>
      </td>
      {MONTHS.map((_, i) => {
        const m = i + 1;
        const tm = g(data.months, m)?.tm || {};
        return (
          <td key={m} className={`py-2.5 px-1 text-right ${tm.closed ? "bg-slate-100/70" : ""}`} data-testid={`tm-cell-${m}`}>
            <button
              className="h-8 w-full flex items-center justify-end gap-1 pr-1.5 text-sm font-semibold text-[#0052CC] hover:underline"
              onClick={() => onDrilldown(m)}
              data-testid={`tm-drilldown-${m}`}
            >
              {tm.incomplete && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex" data-testid={`tm-incomplete-${m}`}>
                      <AlertTriangle className="h-3 w-3 text-[#B45309]" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    Incomplete actual data for {MONTHS[m - 1]} — missing actual revenue: {(tm.missing_actual || []).join(", ") || "T&M"}
                  </TooltipContent>
                </Tooltip>
              )}
              {tm.working_hours_missing && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex" data-testid={`tm-wh-missing-${m}`}>
                      <AlertTriangle className="h-3 w-3 text-[#B91C1C]" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Missing Working Hours for {MONTHS[m - 1]} — T&M cannot be calculated</TooltipContent>
                </Tooltip>
              )}
              {fmtSEK(tm.value)}
            </button>
          </td>
        );
      })}
      <td className="py-2.5 px-2 text-right text-sm text-slate-600" data-testid="tm-ytd">
        {summary?.has_closed_months ? fmtSEK(tmYtd) : "—"}
      </td>
      <td className="py-2.5 px-2 text-right text-sm font-bold text-slate-900" data-testid="tm-fy">{fmtSEK(summary?.tm_fy)}</td>
    </tr>
  );
}

function CategoryRow({ cat, data, summary, isOpen, onToggle, canEdit, onAddSource }) {
  return (
    <tr className="border-b border-slate-200 bg-[#F8FAFC] cursor-pointer" onClick={onToggle} data-testid={`category-row-${cat.key}`}>
      <td className="py-2.5 px-3 sticky left-0 bg-[#F8FAFC] z-10">
        <div className="flex items-center gap-2">
          {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
          <span className="text-sm font-bold text-slate-900">{cat.label}</span>
          {canEdit && (
            <button
              className="text-xs font-semibold text-[#0052CC] hover:underline flex items-center gap-0.5 ml-2"
              onClick={(e) => {
                e.stopPropagation();
                onAddSource(cat.key);
              }}
              data-testid={`add-source-${cat.key}`}
            >
              <Plus className="h-3 w-3" /> Add
            </button>
          )}
        </div>
      </td>
      {MONTHS.map((_, i) => {
        const m = i + 1;
        const cv = g(data.months, m)?.categories?.[cat.key] || {};
        return (
          <td key={m} className={`py-2.5 px-1 text-right ${cv.incomplete ? "bg-[#FFFBEB]" : ""} ${g(data.months, m)?.closed ? "bg-slate-100/70" : ""}`} data-testid={`category-cell-${cat.key}-${m}`}>
            <div className="h-8 flex items-center justify-end gap-1 pr-1.5 text-sm font-semibold text-slate-800">
              {cv.incomplete && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex" data-testid={`category-incomplete-${cat.key}-${m}`}>
                      <AlertTriangle className="h-3 w-3 text-[#B45309]" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Missing actual: {(cv.missing_actual || []).join(", ")}</TooltipContent>
                </Tooltip>
              )}
              {fmtSEK(cv.value)}
            </div>
          </td>
        );
      })}
      <td className="py-2.5 px-2 text-right text-sm text-slate-600">—</td>
      <td className="py-2.5 px-2 text-right text-sm font-bold text-slate-900" data-testid={`category-fy-${cat.key}`}>
        {fmtSEK(summary?.[CATEGORY_FY_KEY[cat.key]])}
      </td>
    </tr>
  );
}

function SourceRow({ source: s, summary, canEdit, onCommit, onOpen }) {
  return (
    <tr className={`border-b border-slate-100 ${s.active ? "" : "opacity-50"}`} data-testid={`source-row-${s.id}`}>
      <td className="py-1 px-3 sticky left-0 bg-white z-10">
        <div className="flex items-center gap-2 pl-6">
          <button
            className="text-sm text-[#0052CC] font-medium hover:underline text-left"
            onClick={onOpen}
            data-testid={`open-source-${s.id}`}
          >
            ↳ {s.name}
          </button>
          {s.customer_name && <span className="text-xs text-slate-400">{s.customer_name}</span>}
          {!s.active && <span className="text-[10px] font-semibold text-slate-400 uppercase">inactive</span>}
        </div>
      </td>
      {MONTHS.map((_, i) => {
        const m = i + 1;
        const cell = g(s.months, m) || {};
        return (
          <AmountCell
            key={m}
            value={cell.locked ? cell.actual_amount : cell.forecast_amount}
            locked={cell.locked}
            missing={cell.missing_actual}
            canEdit={canEdit}
            muted={!s.active}
            onCommit={(v) => onCommit(s.id, m, v)}
            testId={`source-cell-${s.id}-${m}`}
          />
        );
      })}
      <td className="py-1 px-2 text-right text-xs text-slate-500" data-testid={`source-ytd-${s.id}`}>
        {summary?.has_closed_months ? fmtSEK(s.ytd) : "—"}
      </td>
      <td className="py-1 px-2 text-right text-sm font-semibold text-slate-800" data-testid={`source-fy-${s.id}`}>{fmtSEK(s.fy)}</td>
    </tr>
  );
}

function TotalRow({ data, summary }) {
  return (
    <tr className="border-t-2 border-slate-300 bg-white" data-testid="total-sales-row">
      <td className="py-3 px-3 sticky left-0 bg-white z-10">
        <span className="text-sm font-extrabold text-slate-900">TOTAL SALES</span>
      </td>
      {MONTHS.map((_, i) => {
        const m = i + 1;
        const md = g(data.months, m) || {};
        return (
          <td key={m} className={`py-3 px-1 text-right ${md.closed ? "bg-slate-100/70" : ""}`} data-testid={`total-cell-${m}`}>
            <div className="h-8 flex items-center justify-end gap-1 pr-1.5 text-sm font-extrabold text-slate-900">
              {md.incomplete && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex" data-testid={`total-incomplete-${m}`}>
                      <AlertTriangle className="h-3 w-3 text-[#B45309]" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Incomplete actual data for {MONTHS[m - 1]} — see category warnings</TooltipContent>
                </Tooltip>
              )}
              {fmtSEK(md.total_sales)}
            </div>
          </td>
        );
      })}
      <td className="py-3 px-2 text-right text-sm font-semibold text-slate-700" data-testid="total-ytd">
        {summary?.has_closed_months ? fmtSEK(summary.ytd_actual) : "—"}
      </td>
      <td className="py-3 px-2 text-right text-sm font-extrabold text-slate-900" data-testid="total-fy">
        {fmtSEK(summary?.expected_total_sales)}
      </td>
    </tr>
  );
}

export default function RevenueGrid({
  data,
  summary,
  loading,
  visibleSources,
  expanded,
  onToggleCategory,
  canEdit,
  onCommit,
  onAddSource,
  onOpenSource,
  onTmDrilldown,
}) {
  const tmYtd = summary?.has_closed_months
    ? Object.values(data.months).reduce((sum, md) => sum + (md.closed ? md.tm.value : 0), 0)
    : null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="revenue-grid">
      {loading && (
        <div className="py-16 flex justify-center" data-testid="rev-loading">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      )}
      {!loading && data && (
        <table className="w-full min-w-[1300px] border-collapse">
          <thead>
            <tr className="bg-secondary/50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3 sticky left-0 bg-secondary/50 min-w-64 z-10">Revenue</th>
              {MONTHS.map((label, i) => {
                const closed = g(data.periods, i + 1)?.status === "CLOSED";
                return (
                  <th key={label} className={`text-right text-xs font-semibold uppercase tracking-wider py-2.5 px-1 w-24 ${closed ? "text-slate-400 bg-slate-100/70" : "text-slate-500"}`}>
                    <div className="flex items-center justify-end gap-1 pr-1.5">
                      {closed && <Lock className="h-3 w-3" />}
                      {label}
                    </div>
                  </th>
                );
              })}
              <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-2 w-24">YTD</th>
              <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-2 w-28">FY</th>
            </tr>
          </thead>
          <tbody>
            <TmRow data={data} summary={summary} tmYtd={tmYtd} onDrilldown={onTmDrilldown} />

            {CATEGORIES.map((cat) => {
              const catSources = visibleSources.filter((s) => s.category === cat.key);
              const isOpen = expanded[cat.key];
              return [
                <CategoryRow
                  key={cat.key}
                  cat={cat}
                  data={data}
                  summary={summary}
                  isOpen={isOpen}
                  onToggle={() => onToggleCategory(cat.key)}
                  canEdit={canEdit}
                  onAddSource={onAddSource}
                />,
                isOpen &&
                  catSources.map((s) => (
                    <SourceRow
                      key={s.id}
                      source={s}
                      summary={summary}
                      canEdit={canEdit}
                      onCommit={onCommit}
                      onOpen={() => onOpenSource(s.id)}
                    />
                  )),
                isOpen && catSources.length === 0 && (
                  <tr key={`${cat.key}-empty`} className="border-b border-slate-100">
                    <td colSpan={14} className="py-3 px-3 pl-9 text-xs text-slate-400" data-testid={`category-empty-${cat.key}`}>
                      No {cat.label.toLowerCase()} sources yet{canEdit ? " — use + Add above" : ""}.
                    </td>
                  </tr>
                ),
              ];
            })}

            <TotalRow data={data} summary={summary} />
          </tbody>
        </table>
      )}
    </div>
  );
}
